import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SPECIES } from "@/data/site";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

export default function Species() {
  const { slug } = useParams();
  const s = SPECIES.find((x) => x.slug === slug);

  if (!s)
    return (
      <div className="px-5 pb-32 pt-48 text-center" data-testid="species-not-found">
        <p className="font-display text-4xl">Species not found.</p>
        <Link to="/timber" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-kwila">
          <ArrowLeft size={15} /> Back to Timber
        </Link>
      </div>
    );

  const rows = [
    ["Description", s.blurb],
    ["Appearance & colour", s.tone],
    ["Typical uses", s.uses.join(" · ")],
    ["Durability", "[Detailed durability rating to be confirmed by client]"],
    ["Workability", "[Machining and finishing notes to be confirmed by client]"],
    ["Available sizes", "[Size range to be confirmed — quote to your cutting list]"],
    ["Stock status", "[To be confirmed — contact our Archerfield team]"],
  ];

  return (
    <div data-testid="species-page">
      <section className="px-5 pb-16 pt-32 md:px-10 md:pt-40">
        <div className="mx-auto max-w-[1100px]">
          <Link
            to="/timber"
            data-testid="species-back-link"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-sand transition-colors hover:text-kwila"
          >
            <ArrowLeft size={14} /> Species Index
          </Link>
          <div className="mt-8">
            <Kicker>Timber Species</Kicker>
            <h1 className="mt-4 font-display text-5xl font-light tracking-tight md:text-7xl">
              <MaskedLine delay={0.1}>{s.name}</MaskedLine>
            </h1>
            <p className="mt-3 font-display text-2xl italic text-kwila">{s.latin}</p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg">{s.blurb}</p>
          </div>
        </div>
      </section>

      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto max-w-[1100px]">
          <Reveal>
            <div className="divide-y divide-ink/10 border-y border-ink/10" data-testid="species-details">
              {rows.map(([k, v]) => (
                <div key={k} className="grid gap-2 py-5 md:grid-cols-4 md:gap-6">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-sand">{k}</span>
                  <span className="text-sm leading-relaxed text-ink/80 md:col-span-3 md:text-base">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 rounded-sm border border-forest/30 bg-forest/5 p-6 md:p-8">
            <p className="text-sm leading-relaxed text-ink/75">
              <span className="font-bold text-forest">Responsible sourcing.</span> Mr Kwila is committed to
              responsible timber sourcing. Final certification, legality and chain-of-custody documentation is
              to be supplied by the client and will be published here before launch.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/request-quote"
              data-testid="species-quote-button"
              className="flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila"
            >
              Request {s.name.split(" /")[0]} Quote <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/trade"
              data-testid="species-trade-button"
              className="flex items-center gap-2 rounded-full border border-ink/30 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream"
            >
              Trade Account Enquiry
            </Link>
          </Reveal>
        </div>
      </section>

      <QuoteCTA title={`Buying ${s.name.split(" /")[0]} in volume?`} copy="Send species, sizes, quantities and delivery location. Packs and bulk orders priced on request." />
    </div>
  );
}
